'use client';
import { useCallback, useMemo, useState, useTransition } from 'react';
import {
  normalizeCatalogSearchFilters,
  serializeCatalogSearchFilters,
  type CatalogSearchFilters,
  type NormalizedCatalogSearchFilters
} from './catalog.api';

type UpdateOptions = { preservePage?: boolean };

export const useCatalogFilterState = (
  initial?: CatalogSearchFilters
): {
  filters: NormalizedCatalogSearchFilters;
  updateFilters: (next: CatalogSearchFilters, options?: UpdateOptions) => void;
  setPage: (page: number) => void;
  resetFilters: () => void;
  isPending: boolean;
  queryString: string;
  searchParams: URLSearchParams;
} => {
  const [filters, setFilters] = useState(() =>
    normalizeCatalogSearchFilters(initial)
  );
  const [isPending, startTransition] = useTransition();
  const updateFilters = useCallback(
    (next: CatalogSearchFilters, options?: UpdateOptions) => {
      startTransition(() => {
        setFilters((current) => {
          const merged: CatalogSearchFilters = { ...current, ...next };
          if (!options?.preservePage && next.page === undefined) {
            merged.page = 1;
          }
          return normalizeCatalogSearchFilters(merged);
        });
      });
    },
    []
  );
  const setPage = useCallback(
    (page: number) => {
      updateFilters({ page }, { preservePage: true });
    },
    [updateFilters]
  );
  const resetFilters = useCallback(() => {
    startTransition(() => {
      setFilters(normalizeCatalogSearchFilters());
    });
  }, []);
  const searchParams = useMemo(
    () => serializeCatalogSearchFilters(filters),
    [filters]
  );
  return {
    filters,
    updateFilters,
    setPage,
    resetFilters,
    isPending,
    queryString: searchParams.toString(),
    searchParams
  };
};
